import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ReadonlyURLSearchParams,
  useParams,
  usePathname,
  useSearchParams,
} from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useUserStore as useRestaurantProfileStore } from '@/stores/restuarant/user';
import { useUserStore } from '@/stores/cutomer/user';
import useStore from '@/stores/useStore';
import LocationSelector from './LocationSelector';
import { UserSignIn } from '@/components/ui/Icons';
import CountrySelector from '@/components/shared/CountrySelector';
import HeaderNavLinks from './HeaderNavLinks';
import HeaderProfile from './HeaderProfile';
import HeaderProfileRestaurant from './HeaderProfileRestaurant';
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BRAND_LOGO_ALT_TEXT } from '@/constant/configs';
import { storeFront, restaurant } from '@/lib/utils/routeLinks';
import CategoriesMegaMenu from './CategoriesMegaMenu';
import HeaderOnlineShopSearch from './HeaderOnlineShopSearch';

interface DrawerMenuProps {
  handleMenuClose: () => void;
  showVendorMenu?: boolean;
}

const DrawerMenu = ({
  handleMenuClose,
  showVendorMenu = true,
}: DrawerMenuProps) => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const params = useParams();
  const pathname = usePathname();
  const user = useUserStore((state) => state.user);
  const restaurantProfile = useStore(
    useRestaurantProfileStore,
    (state) => state.user
  );

  const t = useTranslations('');

  const locale: string = params.locale as string;
  const [lang] = locale.split('-');

  React.useEffect(() => {
    setLoading(false);
  }, []);

  const vendorPages = [storeFront.forVendor.en(), storeFront.forVendor.de()];
  const isVendorPage = vendorPages.some((page) => pathname.includes(page));
  const loginPath = isVendorPage
    ? restaurant.login.de()
    : storeFront.login.en();
  const isHomePage = pathname === `/${locale}`;
  const isCompanyPage = [
    storeFront.forCompany.de(),
    storeFront.forCompany.en(),
  ].some((route) => pathname === `/${locale}${route}`);

  //= Get Query Params values
  const searchParams: ReadonlyURLSearchParams = useSearchParams();
  const pageType: string | null = searchParams.get('type');
  const lat: string | null = searchParams.get('lat');
  const lng: string | null = searchParams.get('lng');
  const restaurantSearchPathName = `${pathname}?lat=${lat}&lng=${lng}&type=${pageType}`;

  return (
    <>
      <div className="bg-white py-2">
        <div
          className="fixed inset-0 z-30 bg-[rgba(0,_0,_0,_0.50)]"
          onClick={handleMenuClose}
        />
        <div className="fixed right-0 top-0 z-40 w-full bg-white">
          <div className="relative h-screen w-full  pt-5">
            <div className="border-b border-gray-200">
              <div className="flex items-center justify-between px-5 pb-5">
                <div className="flex items-center justify-between">
                  <Link href={'/'} className="">
                    <Image
                      src="/logo.svg"
                      alt={BRAND_LOGO_ALT_TEXT}
                      width={230}
                      height={41}
                    />
                  </Link>
                </div>
                <button
                  onClick={handleMenuClose}
                  className="rounded-md p-2 shadow"
                  aria-label={t('aria.close_drawer_menu')}
                >
                  <XMarkIcon className="h-6 w-6 stroke-2 text-neutral-400" />
                </button>
              </div>
            </div>
            <div className="mt-5 space-y-5 px-5">
              <div className="location-selector-mobile">
                {restaurantSearchPathName ===
                `/${
                  params.locale
                }${storeFront.restaurants.de()}?lat=${lat}&lng=${lng}&type=delivery` ? (
                  <LocationSelector
                    pathName={restaurantSearchPathName}
                    lat={lat}
                    lng={lng}
                    pageType={pageType}
                    rootClasses="block"
                  />
                ) : null}

                {restaurantSearchPathName ===
                `/${
                  params.locale
                }${storeFront.restaurants.de()}?lat=${lat}&lng=${lng}&type=pickup` ? (
                  <LocationSelector
                    pathName={restaurantSearchPathName}
                    lat={lat}
                    lng={lng}
                  />
                ) : null}
              </div>

              {user ? (
                <HeaderProfile closeMenu={handleMenuClose} />
              ) : restaurantProfile ? (
                <HeaderProfileRestaurant />
              ) : showVendorMenu ? (
                <div className="flex flex-col space-y-4 text-paragraph">
                  <HeaderNavLinks />
                </div>
              ) : null}

              <div className="space-y-4">
                {/* <HeaderStorefrontLinks closeDrawer={handleMenuClose} /> */}
                <HeaderOnlineShopSearch />
                {!isCompanyPage && (
                  <div className="flex xl:hidden">
                    <CategoriesMegaMenu lang={lang as 'en' | 'de'} />
                  </div>
                )}

                {/* New "So funktioniert's" Link */}
                {isHomePage && (
                  <Link
                    href="#howItWorks"
                    className="flex items-center gap-1 text-gray-900 hover:text-black"
                    onClick={(e) => {
                      e.preventDefault();
                      document
                        .getElementById('howItWorks')
                        ?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <div className="">
                      <QuestionMarkCircleIcon className="w-5" />
                    </div>
                    <div>{t('common.how_it_works')}</div>
                  </Link>
                )}

                <CountrySelector />
              </div>
            </div>
            <div className="absolute bottom-40 left-0 right-0 flex items-center justify-center md:bottom-5">
              {loading ? (
                <div className="flex h-[50px] w-[80px] items-center justify-start">
                  <div className="h-[41px] w-[41px] shrink-0 animate-pulse rounded-full bg-slate-600"></div>
                </div>
              ) : (
                <>
                  {!user && !restaurantProfile && (
                    <Link
                      href={loginPath}
                      className=" flex w-[80%] items-center justify-center rounded-[30px] bg-black px-[25px] py-[15px] text-h7/[1.3] font-normal text-white transition-colors hover:bg-primary-700 hover:text-white"
                    >
                      {isVendorPage ? t('restaurant_login') : t('sign_in')}{' '}
                      <UserSignIn className="ml-[11.85px]" />
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DrawerMenu;
